import { Icon } from '@lbcamden/lib-react';
import { formatDMY } from '@lbcamden/shared-utils';
import { FunctionComponent } from 'react';

import { ButtonLink } from '~/client/components/common/Button/ButtonLink';
import { ShowDetails } from '~/client/components/common/ShowDetails';
import {
  useLastSuccessfulPropertyVisitDate,
  usePropertyDetails,
  useAllNotesList,
  useReferenceListDisplayValue,
} from '~/client/hooks/storeHooks';
import { formatGBP } from '~/client/utils/currency';
import { toVisitCreatePage } from '~/client/utils/navigation';
import { getPropertyValidity } from '~/client/utils/properties';
import { Property } from '~/common/types/buildings';

import { HeaderButtonRow, HeaderCautionLink } from '../PageHeader.styled';

interface Props {
  property: Property;
}

const unknownValue = 'Unknown';

export const HeaderDetails: FunctionComponent<Props> = ({ property }) => {
  const referenceListDisplayValue = useReferenceListDisplayValue();
  const lastSuccessfulVisitDate = useLastSuccessfulPropertyVisitDate(property.propertyId);
  const hasCautionNote = useAllNotesList(property.propertyId).some(({ caution }) => caution);
  const {
    payReference,
    rentTotalPayable,
    rentBalance,
    tenancySource,
    tenancyStartDate,
    noResidentsFound,
  } = usePropertyDetails(property.propertyId) || {};

  const { isValid } = getPropertyValidity(property, noResidentsFound);

  if (!isValid) {
    return null;
  }

  return (
    <>
      <dl>
        <dt>Last successful visit:</dt>
        <dd>{lastSuccessfulVisitDate ? formatDMY(lastSuccessfulVisitDate) : '–'}</dd>
        <dt>Tenancy source:</dt>
        <dd>{referenceListDisplayValue('TEN_SRCE', tenancySource || unknownValue)}</dd>
        <dt>Tenancy start date:</dt>
        <dd>{tenancyStartDate ? formatDMY(tenancyStartDate) : unknownValue}</dd>
      </dl>

      <ShowDetails label={(open) => `${open ? 'Hide' : 'Show'} rent details`}>
        <dl>
          <dt>Payment reference:</dt>
          <dd>{payReference || unknownValue}</dd>
          <dt>Rent balance:</dt>
          <dd>{formatGBP(rentBalance, { default: unknownValue })}</dd>
          <dt>Rent total payable:</dt>
          <dd>{formatGBP(rentTotalPayable, { default: unknownValue })}</dd>
        </dl>
      </ShowDetails>

      <HeaderButtonRow>
        <span />
        <ButtonLink to={toVisitCreatePage(property.propertyId)}>Log visit</ButtonLink>
        <span>
          {hasCautionNote && (
            <HeaderCautionLink href="#notes">
              <Icon icon="ui/warning" width="1.15rem" />
            </HeaderCautionLink>
          )}
        </span>
      </HeaderButtonRow>
    </>
  );
};
